// ============================================
// AbsorptionSettingsMenu.jsx
// Settings popup for the Absorption indicator (outlines footprint cells where
// heavy one-sided volume was rejected).
//
// Shared menu shell (menu/ChartMenu): frame, fit, outside-click / Esc and
// every style live in one place now (the CHART MENUS block in styles.css).
// The old z-index-20 constraint is gone too: ColorPicker's panel sits at
// --menu-picker-z, above the shared --menu-z, so no menu can cover it.
//
// config shape (owned by the chart, edited live — no APPLY, because every
// control is a visual property you judge by looking at the chart):
//   { buyColor, sellColor, weight, minMult, showLiq, zones, zoneColor }
// ============================================
import ChartMenu, { MenuRow, MenuSwatch, MenuWeight, MenuSelect, MenuDivider, MenuToggle } from './menu/ChartMenu'

const WEIGHTS = [1, 2, 3, 4]
const MULTS = [1.5, 2, 3, 4, 6].map(m => ({ value: m, label: `${m}× median` }))

export default function AbsorptionSettingsMenu({ show, x, y, openLeft, config, onUpdate, onClose }) {
  if (!show) return null
  const set = (patch) => onUpdate({ ...config, ...patch })

  return (
    <ChartMenu anchor={{ x, y }} openLeft={openLeft} onClose={onClose}>
      <MenuRow>
        {/* Two colours because absorption has a side: a level that ate SELLS
            and held is a bid defending — the opposite claim from a level
            that ate buys. One colour would make them indistinguishable,
            which is the whole signal. */}
        <MenuSwatch value={config.buyColor} onChange={(c) => set({ buyColor: c })} title="Bid absorb — sells eaten, level held" />
        <MenuDivider />
        <MenuSwatch value={config.sellColor} onChange={(c) => set({ sellColor: c })} title="Ask absorb — buys eaten, level held" />
        <MenuDivider />
        <MenuWeight value={config.weight} weights={WEIGHTS} onChange={(w) => set({ weight: w })} />
        <MenuDivider />
        {/* MIN VOL — the filter. Not cosmetic: it decides what counts as
            absorption at all. Expressed as a multiple of the median visible
            cell because an absolute threshold is meaningless across a
            156-mcap mint and ETH. */}
        <MenuSelect value={config.minMult} options={MULTS}
          title="Minimum one-sided volume, as a multiple of the median visible cell"
          renderValue={() => `${config.minMult}×`}
          onChange={(m) => set({ minMult: m })} />
        <MenuDivider />
        {/* absorb-liq4: liquidity reaction labels ('Liq ↑6s') beside cells. */}
        <MenuToggle on={config.showLiq !== false} onChange={(v) => set({ showLiq: v })}
          title="Show liquidity reaction labels (Liq ↑/↓ seconds) beside absorption cells">
          Liq
        </MenuToggle>
        <MenuDivider />
        {/* absorb-zones-20261019: strong past absorption levels as zones. */}
        <MenuToggle on={!!config.zones} onChange={(v) => set({ zones: v })}
          title="Show zones at past absorption levels with above-average strength">
          Zones
        </MenuToggle>
        <MenuSwatch value={config.zoneColor || '#ffd166'} onChange={(c) => set({ zoneColor: c })} title="Zone colour" />
      </MenuRow>
    </ChartMenu>
  )
}
